/**
 * useMultiStepAgentStream — run control for debug sessions (a workflow step, the prompt builder, an entity assistant),
 * one per key. A session's transcript is its thread: the engine writes every turn to a conversation and names it on
 * the stream (`threadConversationId`), and the drawer renders that conversation with the unified conversation
 * components (`DebugSessionChat`). This hook keeps only what the conversation does not: the run's state, its id for
 * follow-ups and run controls (pause, resume, stop), and the latest answer for the caller that applies it.
 */
import { useState, useRef, useCallback, useEffect } from 'react';

import {
  executeStepStream,
  getActiveRun,
  pauseExecution,
  reconnectToRun,
  resumeExecution,
  sendFollowUpMessage,
  stopExecution,
} from '../generated/api/sdk.gen.js';
import { sseFetch } from '../generated/api-client.js';

/** The options a generated SSE operation takes for its events. */
type SseHandlers = Pick<NonNullable<Parameters<typeof executeStepStream>[0]>, 'onSseEvent' | 'onSseError'>;

/** The fields the agent stream's events carry (`status`, `text`, `complete`, `error`, `session-expired`). */
interface AgentSseData {
  status?: string;
  runId?: string;
  agentId?: string;
  model?: string;
  delta?: string;
  content?: string;
  cost?: number;
  durationMs?: number;
  promptTokens?: number;
  completionTokens?: number;
  message?: string;
  /** The conversation the session's turns are written to (`debug:status`). */
  threadConversationId?: number;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export interface StepSession {
  /** The conversation the session is written to, once the engine has opened it. */
  threadConversationId: number | null;
  /** The latest answer, whole, for a caller that applies it (a form, a prompt). */
  result: string | null;
  status: string | null;
  running: boolean;
  paused: boolean;
  currentRunId: string | null;
  agentId: string | null;
  model: string | null;
  error: string | null;
}

export interface ExecuteParams {
  templateSlug: string;
  input?: unknown;
  config?: Record<string, unknown>;
  systemPromptOverride?: string;
  userPromptTemplate?: string;
  /** Force a fresh CLI session (no resume). Used by prompt builder. */
  freshSession?: boolean;
  /** Disable all tools. Used by prompt builder for pure conversation. */
  noTools?: boolean;
  /** Inputs the engine's prompt builder uses to render the system prompt
   *  (Spec 50 §3.1). Passed through to the debug:execute-step command as
   *  `context_spec`. Prefer this over `systemPromptOverride` — keeps catalog
   *  lookups next to the DB. */
  contextSpec?: Record<string, unknown>;
}

/**
 * What a session is remembered by across a page reload, per key, in this tab: its run (to reconnect to while it still
 * runs) and its thread (to show what was said). A per-viewer convenience: an empty or blocked storage only means the
 * session starts fresh.
 */
interface RememberedSession {
  runId: string;
  threadConversationId: number | null;
}

const STORAGE_PREFIX = 'conflui-debug-session:';

function rememberedSession(stepId: string): RememberedSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + stepId);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') return null;
    const { runId, threadConversationId } = value as Record<string, unknown>;
    if (typeof runId !== 'string' || runId === '') return null;
    return { runId, threadConversationId: typeof threadConversationId === 'number' ? threadConversationId : null };
  } catch {
    return null;
  }
}

function rememberSession(stepId: string, session: RememberedSession): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + stepId, JSON.stringify(session));
  } catch {
    // Storage blocked or full: the session is simply not remembered.
  }
}

function forgetSession(stepId: string): void {
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + stepId);
  } catch {
    // Nothing to forget.
  }
}

interface InternalSession extends StepSession {
  abortController: AbortController | null;
  fullTextRef: string;
}

const emptySession = (): InternalSession => ({
  threadConversationId: null,
  result: null,
  status: null,
  running: false,
  paused: false,
  currentRunId: null,
  agentId: null,
  model: null,
  error: null,
  abortController: null,
  fullTextRef: '',
});

export interface MultiStepAgentStream {
  activeStepId: string | null;
  activeSession: StepSession | null;

  selectStep: (stepId: string | null) => void;
  execute: (stepId: string, params: ExecuteParams) => Promise<void>;
  sendFollowUp: (stepId: string, message: string) => Promise<void>;
  stop: () => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  reset: (stepId: string) => void;
  resetAll: () => void;

  getSession: (stepId: string) => StepSession | undefined;
  isAnyRunning: () => boolean;
}

export function useMultiStepAgentStream(): MultiStepAgentStream {
  const sessionsRef = useRef<Map<string, InternalSession>>(new Map());
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const activeStepIdRef = useRef<string | null>(activeStepId);
  activeStepIdRef.current = activeStepId;
  const [, setRenderTick] = useState(0); // force re-render

  const forceUpdate = () => setRenderTick((t) => t + 1);

  // Get or create session for a step
  const getOrCreate = (stepId: string): InternalSession => {
    if (!sessionsRef.current.has(stepId)) {
      sessionsRef.current.set(stepId, emptySession());
    }
    return sessionsRef.current.get(stepId)!;
  };

  // Update a session field and trigger re-render if active
  // Uses ref to avoid stale closures when called from async operations
  const updateSession = (stepId: string, updates: Partial<InternalSession>) => {
    const session = getOrCreate(stepId);
    Object.assign(session, updates);
    const isActive = stepId === activeStepIdRef.current;
    if (isActive) forceUpdate();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      for (const session of sessionsRef.current.values()) {
        session.abortController?.abort();
      }
    };
  }, []);

  // SSE consumption: one generated SSE operation; each event updates the step's session.
  const consumeSSE = async (
    stepId: string,
    open: (handlers: SseHandlers) => Promise<{ stream: AsyncIterable<unknown> }>,
  ) => {
    const session = getOrCreate(stepId);
    session.fullTextRef = '';
    updateSession(stepId, {
      running: true,
      paused: false,
      error: null,
    });

    const handleEvent = (event: string, data: AgentSseData) => {
      switch (event) {
        case 'status':
          updateSession(stepId, {
            status: data.status ?? 'running',
            currentRunId: data.runId ?? session.currentRunId,
            agentId: data.agentId ?? session.agentId,
            model: data.model ?? session.model,
            threadConversationId: data.threadConversationId ?? session.threadConversationId,
          });
          if (session.currentRunId) {
            rememberSession(stepId, {
              runId: session.currentRunId,
              threadConversationId: session.threadConversationId,
            });
          }
          break;
        case 'text':
          // The thread shows the answer as it is written; kept here only in case the stream ends without `complete`.
          session.fullTextRef += data.delta ?? '';
          break;
        case 'complete':
          updateSession(stepId, { status: 'complete', result: data.content ?? session.fullTextRef });
          break;
        case 'error':
          updateSession(stepId, { error: data.message ?? 'Unknown error', status: 'error' });
          break;
        case 'session-expired': {
          const expiredSession = getOrCreate(stepId);
          expiredSession.abortController?.abort();
          updateSession(stepId, {
            error: data.message ?? 'Your conversation expired. Start a new conversation to continue.',
            status: 'error',
            running: false,
            paused: false,
            abortController: null,
          });
          break;
        }
      }
    };

    let failure: unknown = null;
    try {
      const { stream } = await open({
        onSseEvent: ({ data, event }) => {
          if (typeof data === 'object' && data !== null) handleEvent(event ?? '', data as AgentSseData);
        },
        onSseError: (error) => {
          failure = error;
        },
      });
      // The events arrive through `onSseEvent`; iterating drives the connection until it ends or is aborted.
      for await (const _chunk of stream) {
        /* consumed by onSseEvent */
      }
      if (failure && !isAbort(failure)) throw failure;
    } finally {
      // If the stream ended without a 'complete' event but we have content,
      // promote the streamed text to a completed message so it's not lost.
      const s = getOrCreate(stepId);
      if (s.status !== 'complete' && s.fullTextRef) {
        updateSession(stepId, { status: 'complete', result: s.fullTextRef, running: false, paused: false });
      } else {
        // If stream ended with no events and status is still transient (starting/running),
        // restore to 'complete' so the follow-up input remains visible.
        const updates: Partial<InternalSession> = { running: false, paused: false };
        if (s.status === 'running' || s.status === 'starting') {
          updates.status = 'complete';
        }
        updateSession(stepId, updates);
      }
    }
  };

  /**
   * A session this tab ran before a reload: its thread is shown again, and while its run still runs the stream is
   * picked up where it is (`reconnectToRun`), so its run controls and its answer work as before the reload.
   */
  const restore = async (stepId: string) => {
    if (sessionsRef.current.has(stepId)) return;
    const remembered = rememberedSession(stepId);
    if (!remembered) return;
    updateSession(stepId, {
      currentRunId: remembered.runId,
      threadConversationId: remembered.threadConversationId,
      status: 'reconnecting',
    });
    try {
      const { data } = await getActiveRun({ throwOnError: true });
      if (!data.active || data.runId !== remembered.runId) {
        updateSession(stepId, { status: 'complete' });
        return;
      }
      const session = getOrCreate(stepId);
      session.abortController = new AbortController();
      const signal = session.abortController.signal;
      await consumeSSE(stepId, (handlers) =>
        reconnectToRun({
          path: { runId: remembered.runId },
          signal,
          sseMaxRetryAttempts: 1,
          fetch: sseFetch,
          ...handlers,
        }),
      );
    } catch (err) {
      if (!isAbort(err)) updateSession(stepId, { status: 'complete', running: false });
    }
  };

  const selectStep = useCallback((stepId: string | null) => {
    // Update ref IMMEDIATELY so that any subsequent execute() / updateSession()
    // in the same synchronous call chain sees the correct stepId.
    // Without this, the batched setActiveStepId only updates on the next render,
    // causing updateSession's forceUpdate() guard to fail.
    activeStepIdRef.current = stepId;
    setActiveStepId(stepId);
    if (stepId) void restore(stepId);
  }, []);

  const execute = useCallback(
    async (stepId: string, params: ExecuteParams) => {
      const session = getOrCreate(stepId);

      // Abort any existing stream for this step
      session.abortController?.abort();
      session.abortController = new AbortController();

      // A new run is a new thread: the engine names it once the run has opened it.
      updateSession(stepId, { status: 'starting', threadConversationId: null, result: null, currentRunId: null });

      try {
        const signal = session.abortController.signal;
        await consumeSSE(stepId, (handlers) =>
          executeStepStream({ body: params, signal, sseMaxRetryAttempts: 1, fetch: sseFetch, ...handlers }),
        );
      } catch (err) {
        const error = err as Error;
        console.error('[stream:execute] error', { name: error.name, message: error.message });
        if (error.name !== 'AbortError') {
          updateSession(stepId, { error: error.message, status: 'error', running: false });
        }
      }
    },
    [activeStepId],
  );

  const sendFollowUp = useCallback(
    async (stepId: string, message: string) => {
      const session = getOrCreate(stepId);
      if (!session.currentRunId) throw new Error('No active run for this step');

      session.abortController?.abort();
      session.abortController = new AbortController();

      // The engine writes the message to the session's thread; the conversation shows it from there.
      updateSession(stepId, { status: 'running' });

      try {
        const runId = session.currentRunId;
        const signal = session.abortController.signal;
        await consumeSSE(stepId, (handlers) =>
          sendFollowUpMessage({
            path: { runId },
            body: { message },
            signal,
            sseMaxRetryAttempts: 1,
            fetch: sseFetch,
            ...handlers,
          }),
        );
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          updateSession(stepId, { error: (err as Error).message, status: 'error', running: false });
        }
      }
    },
    [activeStepId],
  );

  const stop = useCallback(async () => {
    try {
      await stopExecution({ throwOnError: true });
      if (activeStepId) {
        const session = getOrCreate(activeStepId);
        session.abortController?.abort();
        updateSession(activeStepId, { running: false, status: 'stopped' });
      }
    } catch {}
  }, [activeStepId]);

  const pause = useCallback(async () => {
    try {
      await pauseExecution({ throwOnError: true });
      if (activeStepId) updateSession(activeStepId, { paused: true });
    } catch {}
  }, [activeStepId]);

  const resume = useCallback(async () => {
    try {
      await resumeExecution({ throwOnError: true });
      if (activeStepId) updateSession(activeStepId, { paused: false });
    } catch {}
  }, [activeStepId]);

  const reset = useCallback(
    (stepId: string) => {
      const session = sessionsRef.current.get(stepId);
      forgetSession(stepId);
      if (session) {
        session.abortController?.abort();
        sessionsRef.current.set(stepId, emptySession());
        if (stepId === activeStepId) forceUpdate();
      }
    },
    [activeStepId],
  );

  const resetAll = useCallback(() => {
    for (const session of sessionsRef.current.values()) {
      session.abortController?.abort();
    }
    sessionsRef.current.clear();
    forceUpdate();
  }, []);

  const getSession = useCallback((stepId: string): StepSession | undefined => {
    return sessionsRef.current.get(stepId);
  }, []);

  const isAnyRunning = useCallback((): boolean => {
    for (const session of sessionsRef.current.values()) {
      if (session.running) return true;
    }
    return false;
  }, []);

  // Get active session (snapshot for rendering)
  const activeSession: StepSession | null = activeStepId ? (sessionsRef.current.get(activeStepId) ?? null) : null;

  // Return a STABLE object reference — properties are updated in-place every render.
  // This ensures that any code capturing this object (e.g. useImperativeHandle closures)
  // always accesses the latest methods via property lookup, avoiding stale closures.
  const stableReturn = useRef<MultiStepAgentStream>(null!);
  if (!stableReturn.current) {
    stableReturn.current = {} as MultiStepAgentStream;
  }
  Object.assign(stableReturn.current, {
    activeStepId,
    activeSession,
    selectStep,
    execute,
    sendFollowUp,
    stop,
    pause,
    resume,
    reset,
    resetAll,
    getSession,
    isAnyRunning,
  });
  return stableReturn.current;
}
